// Scout Card Types

export type TechnologyCategory = 
  | 'AI/ML' | 'Aerospace' | 'Biotech' | 'Cybersecurity' | 'Defense Tech'
  | 'Energy' | 'Hardware' | 'IoT' | 'Maritime' | 'Robotics' | 'Space' | 'Software' | 'Other';

export type CompanyStage = 
  | 'Pre-Seed' | 'Seed' | 'Series A' | 'Series B' | 'Series C+' | 'Growth' | 'Public';

export type RevenueStatus = 
  | 'Pre-Revenue' | 'Early Revenue' | 'Revenue Generating' | 'Profitable';

export type PrimaryMarket = 
  | 'B2B' | 'B2C' | 'B2G' | 'Enterprise' | 'Consumer' | 'Government';

export interface ScoutCard {
  id: string;
  company_name: string;
  short_summary: string;
  website: string | null;
  headquarters_location: string;
  year_founded: number;
  technology_category: TechnologyCategory;
  dual_use_indicator: boolean;
  primary_market: PrimaryMarket;
  secondary_markets: string[];
  government_defense_relevance: string | null;
  company_stage: CompanyStage;
  trl: number | null;
  trl_description: string | null;
  revenue_status: RevenueStatus;
  program_cohort: string | null;
  assigned_scout: string | null;
  primary_contact_name: string;
  primary_contact_email: string;
  title_position: string | null;
  phone: string | null;
  date_of_birth: string | null;
  gender: string | null;
  address: string | null;
  city: string | null;
  state_province: string | null;
  zip_postal_code: string | null;
  linkedin_ceo: string | null;
  linkedin_company: string | null;
  product_info_link: string | null;
  product_description: string | null;
  internal_notes: string | null;
  logo_url: string | null;
  sbir_sttr_received: boolean;
  sbir_sttr_details: string | null;
  created_at: string;
  updated_at: string;
}

export interface ScoutCardFile {
  id: string;
  scout_card_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number | null;
  uploaded_by: string | null;
  created_at: string;
}

export interface ScoutCardIntakeFile {
  id: string;
  scout_card_id: string;
  file_name: string;
  file_path: string;
  file_type: string;
  file_size: number | null;
  created_at: string;
}

export interface Profile {
  id: string;
  user_id: string;
  full_name: string | null;
  role: 'admin';
  created_at: string;
  updated_at: string;
}

// Form data for intake
export interface IntakeFormData {
  // Phase 1: Personal Information
  full_name: string;
  title_position: string;
  company_name: string;
  email: string;
  phone: string;
  date_of_birth: string;
  gender: string;
  address: string;
  city: string;
  state_province: string;
  zip_postal_code: string;
  nearest_parish: string;
  nearest_parish_other: string;
  cluster_focus_areas: string[];
  cluster_focus_other: string;
  referral_source: string;
  referral_partner: string;
  referral_other: string;
  referral_specific?: string;
  prior_services: string[];
  prior_services_other: string;
  // Company Details
  technology_category: string;
  company_stage: string;
  revenue_status: string;
  primary_market: string;
  year_founded: number;
  dual_use_indicator: boolean;
  // Phase 2: TRL Assessment
  trl: number;
  trl_description: string;
  // Phase 3: Access Request
  linkedin_ceo: string;
  linkedin_company: string;
  website: string;
  product_info_link: string;
  short_summary: string;
  product_description: string;
}

export const GENDER_OPTIONS = ['Male', 'Female', 'Other', 'Prefer not to say'];

export const TRL_DESCRIPTIONS: Record<number, string> = {
  1: 'Basic principles observed and reported',
  2: 'Technology concept and/or application formulated',
  3: 'Analytical and experimental critical function and/or proof of concept',
  4: 'Component validation in a laboratory environment',
  5: 'Component validation in a relevant environment',
  6: 'System/subsystem model or prototype demonstration in a relevant environment',
  7: 'System prototype demonstration in an operational environment',
  8: 'Actual system completed and qualified through test and demonstration',
  9: 'Actual system proven through successful mission operations',
};

// Dropdown options
export const TECHNOLOGY_CATEGORIES: TechnologyCategory[] = [
  'AI/ML', 'Aerospace', 'Biotech', 'Cybersecurity', 'Defense Tech',
  'Energy', 'Hardware', 'IoT', 'Maritime', 'Robotics', 'Space', 'Software', 'Other'
];

export const COMPANY_STAGES: CompanyStage[] = [
  'Pre-Seed', 'Seed', 'Series A', 'Series B', 'Series C+', 'Growth', 'Public'
];

export const REVENUE_STATUSES: RevenueStatus[] = [
  'Pre-Revenue', 'Early Revenue', 'Revenue Generating', 'Profitable'
];

export const PRIMARY_MARKETS: PrimaryMarket[] = [
  'B2B', 'B2C', 'B2G', 'Enterprise', 'Consumer', 'Government'
];

export const PROGRAMS: string[] = [
  'Accelerator Cohort 2024', 'Defense Innovation Program', 'Space Tech Initiative',
  'Cyber Security Bootcamp', 'AI/ML Program', 'General Submission'
];

export const TRL_LEVELS = [1, 2, 3, 4, 5, 6, 7, 8, 9];

// IP Scout Card Types
export interface IPScoutCard {
  id: string;
  title: string;
  description: string | null;
  sector: string;
  ip_type: string | null;
  status: string;
  originating_source: string | null;
  reference_number: string | null;
  contact_name: string | null;
  contact_email: string | null;
  notes: string | null;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export const IP_SECTORS = [
  'Department of Defense',
  'Department of Energy',
  'Department of Homeland Security',
  'Intelligence Community',
  'NASA / Space',
  'Health & Human Services',
  'Transportation',
  'Cybersecurity',
  'Maritime',
  'Universities',
  'Other',
] as const;

export const IP_TYPES = ['Patent', 'Trade Secret', 'License', 'Software', 'Other'] as const;

export const IP_STATUSES = ['available', 'licensed', 'pending', 'archived'] as const;
